/**
 * Role (access group) helpers for the Team screen: the Driver template and the
 * one-line role summary. Pure, so they can be tested without rendering.
 */
import type { AccessGroup, GroupInput } from "@services/teamService";

/** The capability switches the role editor edits (everything except name/farms). */
export type RoleCaps = Pick<
  GroupInput,
  | "canViewFields"
  | "canRecordLoads"
  | "canCalibrate"
  | "canAccessDevices"
  | "canEdit"
  | "canDelete"
  | "canExport"
  | "clientAccess"
>;

/**
 * The usual driver role (spec §5.1, §7): records loads and calibrates from the
 * driver app on the machines of the selected farms, sees nothing else.
 * Farms are deliberately not part of it — the admin keeps or picks them.
 */
export const DRIVER_TEMPLATE: RoleCaps = {
  canRecordLoads: true,
  canCalibrate: true,
  canViewFields: false,
  canAccessDevices: false,
  canEdit: false,
  canDelete: false,
  canExport: false,
  clientAccess: "app",
};

export interface RoleSummaryLabels {
  allFarms: string;
  farmsCount: string; // "{n} farms"
  fields: string;
  loads: string;
  calibrate: string;
  machines: string;
  edit: string;
  del: string;
  exp: string;
  webOnly: string;
  appOnly: string;
}

/** Farm names are shown up to this many; beyond it, a count. */
const MAX_FARM_NAMES = 2;

/**
 * Short, ordered pieces describing a role, e.g.
 * ["Farm 2", "Loads", "Calibration", "Driver app only"]. Only switched-on capabilities are
 * listed; "Can use" is listed only when it is restricted.
 */
export function roleSummaryParts(
  g: Pick<AccessGroup, "allFarms" | "farmIds"> & RoleCaps,
  farmNameById: (id: string) => string | undefined,
  l: RoleSummaryLabels
): string[] {
  const parts: string[] = [];

  if (g.allFarms) {
    parts.push(l.allFarms);
  } else {
    const names = g.farmIds.map(farmNameById).filter((n): n is string => !!n);
    if (names.length > 0 && names.length === g.farmIds.length && names.length <= MAX_FARM_NAMES) {
      parts.push(names.join(", "));
    } else {
      parts.push(l.farmsCount.replace("{n}", String(g.farmIds.length)));
    }
  }

  if (g.canViewFields) parts.push(l.fields);
  if (g.canRecordLoads) parts.push(l.loads);
  if (g.canCalibrate) parts.push(l.calibrate);
  if (g.canAccessDevices) parts.push(l.machines);
  if (g.canEdit) parts.push(l.edit);
  if (g.canDelete) parts.push(l.del);
  if (g.canExport) parts.push(l.exp);

  if (g.clientAccess === "web") parts.push(l.webOnly);
  if (g.clientAccess === "app") parts.push(l.appOnly);

  return parts;
}
